import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import { PageHeader } from "@/components/shell/PageHeader";
import {
  Gift,
  Copy,
  Check,
  Users,
  DollarSign,
  TrendingUp,
  Wallet,
  ExternalLink,
  ArrowRight,
  Clock,
  UserPlus,
  CreditCard,
  Sparkles,
  Share2,
  ChevronRight,
  Calendar,
  CheckCircle,
  XCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Referral = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("referral");
  const [loading, setLoading] = useState(true);
  const [referralInfo, setReferralInfo] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [copied, setCopied] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);

  const fetchReferralInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/referral/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralInfo(response.data);
    } catch (error) {
      console.error("Error fetching referral info:", error);
    }
  }, [token]);

  const fetchInvitations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/referral/invitations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvitations(response.data.records || []);
    } catch (error) {
      console.error("Error fetching invitations:", error);
    }
  }, [token]);

  const fetchWithdrawals = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/referral/withdrawals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWithdrawals(response.data.records || []);
    } catch (error) {
      console.error("Error fetching withdrawals:", error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchReferralInfo(), fetchInvitations(), fetchWithdrawals()]);
      setLoading(false);
    };
    loadData();
    const interval = setInterval(() => {
      fetchReferralInfo(); fetchInvitations(); fetchWithdrawals();
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchInvitations, fetchReferralInfo, fetchWithdrawals]);

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      alert("Please enter a valid amount");
      return;
    }
    
    setWithdrawing(true);
    try {
      await axios.post(`${API}/referral/withdraw`, {
        amount: parseFloat(withdrawAmount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWithdrawAmount("");
      fetchReferralInfo();
      fetchWithdrawals();
      alert("Successfully transferred to your main balance!");
    } catch (error) {
      alert(error.response?.data?.detail || "Withdrawal failed");
    }
    setWithdrawing(false);
  };

  const getReferralLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/register?ref=${referralInfo?.referral_code || ""}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  const stats = referralInfo?.stats || {};

  return (
    <div className="space-y-6">
      {/* Neo-brutalist dark cyber header */}
      <PageHeader
        testId="referral-banner"
        eyebrow="Up to 10% commission"
        icon={Sparkles}
        title="Commission Rewards"
        subtitle="Invite operators to WizardX and earn recurring payouts on every plan they purchase."
        actions={<span className="flex items-center gap-2 border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-xs font-semibold uppercase tracking-widest text-cyan-200" data-testid="referral-payout-chip"><Gift className="h-4 w-4" />Payouts via Telegram</span>}
      />

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-800 pb-2">
        {[
          { id: "referral", label: "Referral", icon: Gift },
          { id: "invitations", label: "Invitation Record", icon: Users },
          { id: "withdrawals", label: "Withdrawals Record", icon: Wallet }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 transition-all ${
              activeTab === tab.id
                ? "border border-cyan-500/30 border-b-0 bg-cyan-500/10 text-cyan-200"
                : "text-slate-400 hover:text-white"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Referral Tab */}
      {activeTab === "referral" && (
        <div className="space-y-6">
          {/* Referral Link & Code */}
          <div className="card-dark p-6" data-testid="referral-links-section">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Share2 className="w-5 h-5 text-cyan-400" />
              Your Referral Link & Code
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Referral Link */}
              <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                <p className="text-xs text-slate-500 mb-2">Default Referral Link</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={getReferralLink()}
                    readOnly
                    className="input-dark flex-1 text-sm font-mono"
                    data-testid="referral-link-input"
                  />
                  <button
                    onClick={() => copyToClipboard(getReferralLink(), "link")}
                    className="btn-primary px-3 py-2"
                    data-testid="copy-link-btn"
                  >
                    {copied === "link" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              {/* Referral Code */}
              <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                <p className="text-xs text-slate-500 mb-2">Your Referral Code</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={referralInfo?.referral_code || ""}
                    readOnly
                    className="input-dark flex-1 text-lg font-mono font-bold tracking-wider text-center text-cyan-400"
                    data-testid="referral-code-input"
                  />
                  <button
                    onClick={() => copyToClipboard(referralInfo?.referral_code || "", "code")}
                    className="btn-secondary px-3 py-2"
                    data-testid="copy-code-btn"
                  >
                    {copied === "code" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Commission Stats */}
          <div className="card-dark p-6" data-testid="commission-stats">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-400" />
              Contract Transaction
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="border border-green-500/30 bg-green-500/5 p-4 text-center">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1">Today</p>
                <p className="text-2xl font-bold text-green-400" data-testid="today-commission">
                  ${stats.today_commission?.toFixed(2) || "0.00"}
                </p>
              </div>

              <div className="border border-cyan-500/30 bg-cyan-500/5 p-4 text-center">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1">Invites</p>
                <p className="text-2xl font-bold text-cyan-300" data-testid="total-invites">
                  {stats.total_invites || 0}
                </p>
              </div>

              <div className="border border-emerald-500/30 bg-emerald-500/5 p-4 text-center">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1">Total earned</p>
                <p className="text-2xl font-bold text-emerald-300" data-testid="total-commission">
                  ${stats.total_commission?.toFixed(2) || "0.00"}
                </p>
              </div>

              <div className="border border-amber-500/30 bg-amber-500/5 p-4 text-center">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1">Withdrawable</p>
                <p className="text-2xl font-bold text-amber-300" data-testid="withdrawable-amount">
                  ${stats.withdrawable_amount?.toFixed(2) || "0.00"}
                </p>
              </div>
            </div>

            {/* Withdraw Section */}
            {stats.withdrawable_amount > 0 && (
              <div className="flex flex-col sm:flex-row items-center gap-3 border border-amber-500/30 bg-amber-500/5 p-4">
                <div className="flex-1">
                  <p className="text-sm font-semibold uppercase tracking-wider text-amber-300 mb-1">Transfer to main balance</p>
                  <p className="text-xs text-slate-400">Use referral earnings for subscription renewals.</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    placeholder="Amount"
                    max={stats.withdrawable_amount}
                    className="input-dark w-24 text-sm"
                    data-testid="withdraw-amount-input"
                  />
                  <button
                    onClick={handleWithdraw}
                    disabled={withdrawing || !withdrawAmount}
                    className="btn-primary px-4 py-2 disabled:opacity-50"
                    data-testid="withdraw-btn"
                  >
                    {withdrawing ? "..." : "Extract"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Operating Steps */}
          <div className="card-dark p-6" data-testid="operating-steps">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-cyan-400" />
              How It Works
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  step: 1,
                  icon: Share2,
                  title: "Share Your Link",
                  description: "Send your referral link or code to friends",
                  color: "cyan"
                },
                {
                  step: 2,
                  icon: UserPlus,
                  title: "Friend Subscribes",
                  description: "Your friend signs up and purchases a subscription",
                  color: "amber"
                },
                {
                  step: 3,
                  icon: CreditCard,
                  title: "Earn 10% Commission",
                  description: "Get 10% of their subscription amount as commission",
                  color: "green"
                }
              ].map((item, index) => (
                <div key={item.step} className="relative">
                  <div className={`p-4 rounded-xl bg-${item.color}-500/10 border border-${item.color}-500/20`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-8 h-8 rounded-full bg-${item.color}-500/20 flex items-center justify-center text-${item.color}-400 font-bold`}>
                        {item.step}
                      </div>
                      <item.icon className={`w-5 h-5 text-${item.color}-400`} />
                    </div>
                    <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                    <p className="text-xs text-slate-400">{item.description}</p>
                  </div>
                  {index < 2 && (
                    <ChevronRight className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-6 h-6 text-slate-600 z-10" />
                  )}
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-sm text-yellow-400 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                <span><strong>Tip:</strong> Commissions are added to your balance and can be used for subscription renewals!</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Invitations Tab */}
      {activeTab === "invitations" && (
        <div className="card-dark p-6" data-testid="invitations-section">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            Invitation Records
          </h3>
          
          {invitations.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 text-lg mb-2">No invitations yet</p>
              <p className="text-slate-500 text-sm">Share your referral link to start earning!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">User</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase">Commission</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-800 hover:bg-slate-800/30">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white text-sm font-bold">
                            {inv.username?.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-white font-medium">{inv.username}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                          inv.status === "subscribed"
                            ? "bg-green-500/10 text-green-400"
                            : "bg-yellow-500/10 text-yellow-400"
                        }`}>
                          {inv.status === "subscribed" ? (
                            <CheckCircle className="w-3 h-3" />
                          ) : (
                            <Clock className="w-3 h-3" />
                          )}
                          {inv.status === "subscribed" ? "Subscribed" : "Registered"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-mono font-medium ${inv.commission_earned > 0 ? "text-green-400" : "text-slate-500"}`}>
                          ${inv.commission_earned?.toFixed(2) || "0.00"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-sm text-slate-400">
                        {new Date(inv.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Withdrawals Tab */}
      {activeTab === "withdrawals" && (
        <div className="card-dark p-6" data-testid="withdrawals-section">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-orange-400" />
            Withdrawal & Commission Records
          </h3>
          
          {withdrawals.length === 0 ? (
            <div className="text-center py-12">
              <Wallet className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 text-lg mb-2">No records yet</p>
              <p className="text-slate-500 text-sm">Commissions and withdrawals will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Description</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase">Amount</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map((w) => (
                    <tr key={w.id} className="border-b border-slate-800 hover:bg-slate-800/30">
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                          w.type === "referral_commission"
                            ? "bg-green-500/10 text-green-400"
                            : "bg-blue-500/10 text-blue-400"
                        }`}>
                          {w.type === "referral_commission" ? (
                            <><TrendingUp className="w-3 h-3" /> Commission</>
                          ) : (
                            <><Wallet className="w-3 h-3" /> Transfer</>
                          )}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-300">
                        {w.description}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-mono font-medium ${
                          w.type === "referral_commission" ? "text-green-400" : "text-blue-400"
                        }`}>
                          {w.type === "referral_commission" ? "+" : ""}${w.amount?.toFixed(2)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-sm text-slate-400">
                        {new Date(w.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Referral;
